import { ReactElement, useState, KeyboardEvent, ChangeEvent } from 'react'
import { useTranslation } from 'react-i18next'
import Button from 'beam/dist/Button'
import TextField from 'beam/dist/TextField'
import Dialog, { DialogActions, DialogContent, DialogTitle } from 'beam/dist/Dialog'
import { FormControl } from 'beam/dist/Form'
import Snackbar from '../../Common/NguiSnackbar'

const autoQaIdPrefix = 'ForgotPassword-'

interface ForgotPasswordProps {
  open: boolean,
  onClose: () => void,
  onConfirm: (email: string) => $TSFIXME,
  forgotPasswordResult: $TSFIXME,
}
const ForgotPassword = (props: ForgotPasswordProps): ReactElement => {
  const { t } = useTranslation()
  const { open, onClose, onConfirm } = props
  const [emailAddress, setEmailAddress] = useState('')
  const [emailValid, setEmailValid] = useState(false)
  const [snackbarVisible, setSnackbarVisible] = useState(false)
  const [snackbarText, setSnackbarText] = useState('')
  const [emailExpiration, setEmailExpiration] = useState(0)

  const handleKeypress = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'Enter') {
      handlePasswordReset()
    }
  }

  const updateEmailAddress = (event: ChangeEvent<HTMLInputElement>): void => {
    setEmailAddress(event.target.value)
    setEmailValid(emailValidator(event.target.value))
  }

  const handlePasswordReset = (): void => {
    onConfirm(emailAddress).then((result: $TSFIXME) => {
      styleSnackbarForResult(result)
      onClose()
    })
  }

  const emailValidator = (email: string): boolean => {
    // eslint-disable-next-line no-useless-escape -- TODO: remove this eslint-disable or replace this eslint-disable comment with a proper justification
    const re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    return re.test(String(email).toLowerCase())
  }

  const closeSnackbar = (): void => {
    setSnackbarVisible(false)
  }

  /* Changes the snackbar based on the result from the forgot password http request */
  const styleSnackbarForResult = (result: $TSFIXME): void => {
    if (result?.stack) {
      // Request failed
      setSnackbarVisible(true)
      setSnackbarText(t('passwordResetEmailFailure'))
      setEmailExpiration(0)
    } else if (result?.expiration && result.expiration !== emailExpiration) {
      // Request succeeded
      setSnackbarVisible(true)
      setSnackbarText(`${t('passwordResetEmailSentTo')} ${emailAddress}`)
      setEmailExpiration(result.expiration)
    }
  }

  return (
    <div>
      <Snackbar open={snackbarVisible} message={snackbarText} onClose={closeSnackbar} />
      <Dialog open={open} onClose={onClose}>
        <DialogTitle>{t('forgotPassword')}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth onKeyPress={handleKeypress}>
            <TextField
              label={t('emailAddress')}
              onChange={updateEmailAddress}
              inputProps={{
                'auto-qa-id': `${autoQaIdPrefix}input-email`,
                'data-testid': `${autoQaIdPrefix}input-email`,
              }}
            />
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} auto-qa-id={`${autoQaIdPrefix}button-cancel`} data-testid={`${autoQaIdPrefix}button-cancel`}>
            {t('cancel')}
          </Button>
          <Button
            onClick={handlePasswordReset}
            disabled={!emailValid}
            color="secondary"
            auto-qa-id={`${autoQaIdPrefix}button-emailPasswordReset`}
            data-testid={`${autoQaIdPrefix}button-emailPasswordReset`}
          >
            {t('emailPasswordReset')}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

export default ForgotPassword
